import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falCalendarDay } from 'shared/@generated/icons/falCalendarDay';
import { falCommentLines } from 'shared/@generated/icons/falCommentLines';
import { falCreditCard } from 'shared/@generated/icons/falCreditCard';
import { falLocationDot } from 'shared/@generated/icons/falLocationDot';
import { falMessageMedical } from 'shared/@generated/icons/falMessageMedical';
import { falSignature } from 'shared/@generated/icons/falSignature';
import { falStamp } from 'shared/@generated/icons/falStamp';
import { falSyringe } from 'shared/@generated/icons/falSyringe';
import { Platform, View } from 'react-native';
import {
  Props,
  Listing,
  Animal,
  BreederAffiliation,
  PetAge,
  BreedType,
  Pet,
  PetRole,
} from 'shared/@types';
import { Trans, useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { ListingCard } from 'shared/components/listing-card';
import { Gallery, type GalleryRef } from 'shared/components/gallery';
import { useFormatter } from 'shared/library/use-formatter';
import { Button } from 'shared/components/button';
import UserCard from 'shared/components/user-card';
import { MapPosition } from 'shared/components/map-position';
import LikeButton from 'shared/components/like-button';
import { AnimalCard } from 'shared/components/pet-card';
import { PaymentBrands } from 'shared/components/payment-brands';
import { InfoGrid } from 'shared/components/info-grid';
import { ListingLitterInsured } from 'shared/components/listing-litter-insured';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import { buildRoute, useRoute } from 'shared/library/routes/route';
import { PageBuilder } from 'shared/library/page-builder';
import { PetInsuranceWidget } from 'shared/components/pet-insurance-widget';
import { getCountryName } from 'shared/library/i18n/country-name';
import { useLocale } from 'shared/library/i18n/use-locale';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import React from 'react';
import { useIsLoggedIn } from 'shared/state/user';
import { ListingShareModal } from 'shared/components/listing-share-modal';
import { usePetpayBuyerFeatures } from 'shared/library/use-petpay-buyer-features';
import { PageContent } from 'shared/components/page-content';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { useRouter } from 'shared/library/use-router';
import { getBreederImageAsset } from 'shared/library/get-breeder-image';
import { analytics } from 'shared/library/analytics';
import { contactTracking } from 'shared/library/contact-tracking';
import {
  type PedigreeRegistryLink,
  normalizePedigreeRegistrationId,
} from 'shared/library/pedigree';
import { getForSalePets, getParentPet, getParentPets } from 'shared/library/pet-roles';
import { getSafeLanguageCode } from 'shared/library/i18n/languages';
import { getListingBreedTypeLabelKey } from 'shared/library/listing-breed-type';
import { listingFeatureIcons } from 'shared/components/listing-feature-icons';
import {
  getListingPetPedigreeRegistryLink,
  hasListingVerifiedPedigree,
} from 'shared/library/listing-pedigree';
import { BreedName } from 'shared/components/breed-name';
import { ProductFeedbackPrompt } from 'shared/components/product-feedback-prompt';
import { ReportIssueModal } from 'shared/components/report-issue-modal';
import { EvidensiaBookingWidget } from 'shared/components/evidensia-booking-widget';
import { useDevice } from 'shared/library/use-device';
import { useSafeAreaInsets } from 'shared/library/use-safe-area-insets';
import { useSimilarListings } from 'shared/library/use-similar-listings';

interface ShowListingScreenProps extends Props {
  listing: Listing;
  showAds?: boolean;
}

function formatSwedishPostalCode(postalCode?: string | null) {
  if (!postalCode) return postalCode;

  const digits = postalCode.replace(/\s/g, '');

  if (!/^\d{5}$/.test(digits)) return postalCode;

  return `${digits.slice(0, 2)} ${digits.slice(2)}`;
}

function useContactListing(listing: Listing) {
  const isLoggedIn = useIsLoggedIn();
  const route = useRoute();
  const router = useRouter();

  function getListingAnalyticsParams() {
    return {
      listing_animal: listing.animal,
      listing_breed_id: listing.breed_id,
      listing_breed_slug: listing.breed?.slug?.sv ?? listing.breed?.slug?.en,
      listing_breed_type: listing.breed_type,
      listing_pet_age: listing.pet_age,
      listing_price: listing.price,
      listing_currency: listing.currency,
      listing_deleted: !!listing.deleted_at,
      contact_source: 'listing_detail',
    };
  }

  return () => {
    const contactPath = route('listings.contact', { listing: listing.slug });
    const attempt = contactTracking.start({
      listingId: listing.id,
      contactPath,
      loggedIn: isLoggedIn,
      entryPoint: 'listing_detail',
    });
    const analyticsParams = {
      ...getListingAnalyticsParams(),
      ...contactTracking.params(attempt),
      logged_in: isLoggedIn,
    };

    analytics.event('contact_listing_click', analyticsParams);

    if (Platform.OS !== 'web') {
      void router.push(contactPath);
      return;
    }

    window.location.href = contactPath;
  };
}

// The app pins the seller and contact button below the scroll view, like the mobile web bar.
function NativeListingContactBar({ listing }: { listing: Listing }) {
  const { t } = useTranslation('screens');
  const insets = useSafeAreaInsets();
  const handleContactListing = useContactListing(listing);

  if (listing.deleted_at) {
    return null;
  }

  return (
    <View
      className={'border-brand-brown-dark/15 bg-brand-cream-light border-t px-3 pt-2.5'}
      style={{ paddingBottom: Math.max(insets.bottom, 10) }}
      testID={'listing-contact-bar'}
    >
      <View className={'flex-row items-center gap-2'}>
        <View className={'min-w-0 flex-1'}>
          <UserCard user={listing.user} small={true} className={'min-w-0'} />
        </View>
        <Button
          onPress={handleContactListing}
          text={t('listings.show_listing.send_message').split(/\s+/)[0]}
          color={'primary'}
          icon={falCommentLines}
          iconPosition={'left'}
          center={true}
          dense={true}
          className={'shrink-0'}
        />
      </View>
    </View>
  );
}

const ShowListingScreen = (props: ShowListingScreenProps) => (
  <AdAnimalProvider animal={props.listing.animal}>
    <ShowListingContent {...props} />
  </AdAnimalProvider>
);

const ShowListingContent = ({ listing, showAds = true }: ShowListingScreenProps) => {
  const similarListings = useSimilarListings(listing.slug);
  const { t } = useTranslation(['screens', 'components']);
  const countryName = getCountryName(t, listing.user.country);
  const postalCode = formatSwedishPostalCode(listing.user.postal_code);
  const listingLocation = [[postalCode, listing.user.city].filter(Boolean).join(' '), countryName]
    .filter(Boolean)
    .join(', ');
  const compactListingLocation =
    [postalCode, listing.user.city].filter(Boolean).join(' ') || countryName || undefined;
  const { formatCurrency, formatDateAsTimeAgo, formatDateCustom, formatDateAsAge } = useFormatter();
  const { languageCode, countryCode } = useLocale();
  const { width, md } = useDevice();
  const [widgetsBelowGallery, setWidgetsBelowGallery] = React.useState(false);
  const galleryRef = React.useRef<GalleryRef>(null);

  React.useEffect(() => {
    setWidgetsBelowGallery(Platform.OS === 'web' && width >= md);
  }, [width, md]);

  const isLoggedIn = useIsLoggedIn();
  const route = useRoute();
  const isDog = listing.animal === Animal.Dog;
  const isMixedOrCrossbreed = listing.breed_type !== BreedType.Purebred;
  const buyingRouteName = isDog ? 'buying-a-dog' : 'buying-a-cat';
  const listingsRouteName = isDog ? 'listings.dogs' : 'listings.cats';
  const petPayFee = listing.price! * 0.075;
  const finalPayment = listing.price! - listing.deposit!;
  const totalPayment = listing.price! + petPayFee;
  const petpayBuyerFeatures = usePetpayBuyerFeatures({ includeComingSoon: false });
  const breederAffiliation = listing.user.breeder?.affiliation;
  const pickupDate = listing.pickup_date.slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const pickupDateLabel =
    listing.pet_age === PetAge.Newborn && pickupDate >= today
      ? 'earliest_pickup_date'
      : 'pickup_date';
  const defaultBreedRecognitionAffiliation =
    listing.user.country === 'SE'
      ? listing.animal === Animal.Cat
        ? BreederAffiliation.Sverak
        : BreederAffiliation.SwedishKennelClub
      : listing.user.country === 'NO'
        ? listing.animal === Animal.Cat
          ? BreederAffiliation.NorskeRasekattklubbersRiksforbund
          : BreederAffiliation.NorwegianKennelClub
        : listing.user.country === 'DK'
          ? listing.animal === Animal.Cat
            ? BreederAffiliation.FelisDanica
            : BreederAffiliation.DanishKennelClub
          : listing.user.country === 'FI' && listing.animal !== Animal.Cat
            ? BreederAffiliation.FinnishKennelClub
            : null;

  let recognitionAffiliation: BreederAffiliation | null = defaultBreedRecognitionAffiliation;

  switch (breederAffiliation) {
    case BreederAffiliation.SwedishKennelClub:
    case BreederAffiliation.NorwegianKennelClub:
    case BreederAffiliation.NorskeRasekattklubbersRiksforbund:
    case BreederAffiliation.DanishKennelClub:
    case BreederAffiliation.FelisDanica:
    case BreederAffiliation.FinnishKennelClub:
    case BreederAffiliation.Sverak:
      recognitionAffiliation = breederAffiliation;
      break;
  }

  const breedRecognitionAffiliation = recognitionAffiliation
    ? t(`common:enums.breeder_affiliation.${recognitionAffiliation}`)
    : null;

  const forSalePets = getForSalePets(listing.pets);
  const parentPets = getParentPets(listing.pets);
  const mother = getParentPet(parentPets, PetRole.Mother);
  const father = getParentPet(parentPets, PetRole.Father);
  const getParentRegistryLink = (pet?: Pet | null) =>
    getListingPetPedigreeRegistryLink(listing, pet);
  const hasPedigree = hasListingVerifiedPedigree(listing);
  const isRecognizedPureBreed = listing.breed_type === BreedType.Purebred;
  const breedTypeTitle = t(
    `components:listing_breed_type_badge.${getListingBreedTypeLabelKey(listing)}`,
  );

  function getBreedDescription() {
    if (isRecognizedPureBreed) {
      if (breedRecognitionAffiliation) {
        return t('listings.show_listing.recognized_breed_description_with_affiliation', {
          affiliation: breedRecognitionAffiliation,
        });
      }

      return t('listings.show_listing.recognized_breed_description');
    }

    if (listing.breed_type === BreedType.MixedBreed) {
      return t('listings.show_listing.mixed_breed_description');
    }

    if (breedRecognitionAffiliation) {
      return t(`listings.show_listing.${listing.breed_type}_description_with_affiliation`, {
        affiliation: breedRecognitionAffiliation,
      });
    }

    return t(`listings.show_listing.${listing.breed_type}_description`);
  }

  const breedDescription = getBreedDescription();

  const handleContactListing = useContactListing(listing);

  const renderContactListingButton = ({
    dense = false,
    text = t('listings.show_listing.send_message'),
    className,
  }: {
    dense?: boolean;
    text?: string;
    className?: string;
  } = {}) => (
    <Button
      onPress={handleContactListing}
      text={text}
      color={'primary'}
      icon={falCommentLines}
      iconPosition={'left'}
      center={true}
      dense={dense}
      className={className}
    />
  );
  const shortContactButtonText = t('listings.show_listing.send_message').split(/\s+/)[0];

  const sellerContactCard = (
    <View
      className={
        'border-brand-brown-dark/15 gap-3 rounded border bg-white p-3 md:flex-row md:items-center'
      }
    >
      <View className={'flex-1'}>
        <UserCard user={listing.user} />
      </View>

      {renderContactListingButton()}
    </View>
  );

  const mobileSellerContactBar =
    Platform.OS === 'web' && !listing.deleted_at ? (
      <View
        className={`border-brand-brown-dark/15 bg-brand-cream-light fixed left-0 right-0 z-40 border-t px-3 py-2.5 md:hidden ${
          isLoggedIn ? 'bottom-[calc(var(--tabs-h)+var(--safe-bottom))]' : 'bottom-0'
        }`}
        style={
          isLoggedIn ? undefined : ({ paddingBottom: 'calc(0.625rem + var(--safe-bottom))' } as any)
        }
      >
        <View className={'mx-auto w-full min-w-0 max-w-screen-sm flex-row items-center gap-2'}>
          <View className={'min-w-0 flex-1'}>
            <UserCard user={listing.user} small={true} className={'min-w-0'} />
          </View>
          {renderContactListingButton({
            dense: true,
            text: shortContactButtonText,
            className: 'shrink-0',
          })}
        </View>
      </View>
    ) : null;

  const { galleryItems, petImageIndexes } = React.useMemo(() => {
    const pets = [
      ...getForSalePets(listing.pets),
      getParentPet(getParentPets(listing.pets), PetRole.Mother),
      getParentPet(getParentPets(listing.pets), PetRole.Father),
    ];

    const petImageIndexes = new Map<number, number>();
    const galleryItems = [...listing.media_assets];

    pets.forEach((pet) => {
      if (!pet?.avatar?.url) {
        return;
      }

      let roleLabel: string;

      if (pet.role === PetRole.Mother) {
        roleLabel = t('listings.show_listing.mother');
      } else if (pet.role === PetRole.Father) {
        roleLabel = t('listings.show_listing.father');
      } else {
        const animalLabel = listing.animal === Animal.Cat ? Animal.Cat : Animal.Dog;
        const ageLabel =
          listing.pet_age === PetAge.Adult ? `${animalLabel}_one` : `${animalLabel}_young_one`;

        roleLabel = t(`common:enums.animal.${ageLabel}`);
      }

      const petName = pet.name?.trim();

      petImageIndexes.set(pet.id, galleryItems.length);
      galleryItems.push({
        ...pet.avatar,
        description: petName ? `${roleLabel}: ${petName}` : roleLabel,
      });
    });

    return { galleryItems, petImageIndexes };
  }, [listing, t]);

  function getPetImagePress(pet?: Pet | null) {
    const index = pet ? petImageIndexes.get(pet.id) : undefined;

    return index === undefined ? undefined : () => galleryRef.current?.openFullscreen(index);
  }
  const galleryAdSlide = React.useMemo(
    () =>
      showAds ? (
        <View className={'size-full items-center justify-center'}>
          <AdBanner
            className={Platform.OS === 'web' ? 'max-w-screen-sm' : undefined}
            placement={'content'}
          />
        </View>
      ) : null,
    [showAds],
  );
  const showEvidensiaWidget =
    Platform.OS === 'web' &&
    countryCode === 'SE' &&
    listing.user.country === 'SE' &&
    (listing.animal === Animal.Dog || listing.animal === Animal.Cat);
  const partnerWidgets =
    !listing.deleted_at && (languageCode === 'sv' || showEvidensiaWidget) ? (
      <View className={'mt-3 gap-3 md:mt-6 md:gap-6'} testID={'listing-partner-widgets'}>
        {languageCode === 'sv' && <PetInsuranceWidget listing={listing} />}
        {showEvidensiaWidget && (
          <EvidensiaBookingWidget
            key={listing.id}
            focus={'buyer'}
            animal={isDog ? 'dog' : 'cat'}
            breedId={listing.breed_id}
          />
        )}
      </View>
    ) : null;
  const mediaSectionClassName =
    Platform.OS === 'web'
      ? `md:flex-1 lg:basis-[50vw] lg:max-w-[600px] lg:flex-none ${partnerWidgets ? '' : 'md:sticky md:top-[calc(var(--header-h)+1.5rem)]'}`
      : 'md:flex-1';
  const detailsSectionClassName =
    Platform.OS === 'web' ? 'md:w-1/2 lg:w-auto lg:min-w-0 lg:flex-1' : 'md:w-1/2';
  const petsTitle =
    listing.pet_age === PetAge.Adult
      ? t(`common:enums.animal.${listing.animal}`, { count: forSalePets.length })
      : listing.animal === Animal.Dog
        ? t('listings.show_listing.puppies')
        : t('listings.show_listing.kittens');

  const mediaSection = listing.deleted_at ? (
    <View className={mediaSectionClassName} testID={'listing-media-section'}>
      <View
        className={
          Platform.OS === 'web'
            ? 'aspect-4/3 w-full overflow-hidden rounded-lg'
            : 'aspect-4/3 mx-3 mt-3 w-auto overflow-hidden rounded-lg'
        }
      >
        <View className={'absolute inset-0 z-10 items-center justify-center bg-black/30'}>
          <Text className={'text-2xl font-bold text-white md:text-3xl'}>
            {t('listings.show_listing.deleted_listing')}
          </Text>
        </View>

        <Image
          src={listing.media_assets[0]}
          alt={listing.breed_name}
          containerClassName={'size-full'}
          imageClassName={'scale-110 object-cover blur-lg'}
          sizeType={'large'}
        />
      </View>
    </View>
  ) : (
    <View className={mediaSectionClassName} testID={'listing-media-section'}>
      <Gallery
        ref={galleryRef}
        items={galleryItems}
        defaultAlt={listing.title}
        mobileFullBleed={Platform.OS !== 'web'}
        adSlide={galleryAdSlide}
        showThumbnails={false}
        showDots={true}
        overlay={
          <View className={'flex-row flex-wrap gap-1.5'}>
            <LikeButton
              modelId={listing.id}
              modelType={'App\\Models\\Listing'}
              withText={true}
              textVariant={'like'}
              className={'!border-0 shadow'}
              testID={'listing-like-button'}
            />
            <ListingShareModal listing={listing} className={'!border-0 shadow'} />
            <ReportIssueModal
              subject={'listing'}
              modelId={listing.id}
              url={route('listings.show', { listing: listing.slug }, { includeDomain: true })}
              iconOnly={true}
              className={'!rounded !p-2.5 shadow'}
              testID={'report-listing'}
            />
          </View>
        }
      />
      {widgetsBelowGallery && partnerWidgets}
    </View>
  );

  const detailsSection = (
    <View className={detailsSectionClassName} testID={'listing-details-section'}>
      <Breadcrumb
        className={'mb-1.5'}
        items={[
          {
            text: t(
              isDog
                ? 'web:layout.footer.sections.dogs.buying_a_dog'
                : 'web:layout.footer.sections.cats.buying_a_cat',
            ),
            href: route(buyingRouteName),
          },
          {
            text: t(
              isDog
                ? 'web:layout.footer.sections.dogs.dogs_for_sale'
                : 'web:layout.footer.sections.cats.cats_for_sale',
            ),
            href: route(listingsRouteName),
          },
          isMixedOrCrossbreed
            ? {
                text: t('listings.show_listing.mixed_breeds_breadcrumb'),
                href: route(listingsRouteName, undefined, {
                  query: { breed_type: listing.breed_type },
                }),
              }
            : {
                text: listing.breed_name,
                href: listing.breed
                  ? route('breeds.show.listings', { breed: listing.breed.slug })
                  : undefined,
              },
        ]}
      />
      <H1
        title={
          <BreedName
            name={listing.breed_name}
            animal={listing.animal}
            breedType={listing.breed_type}
            hasVerifiedPedigree={hasPedigree}
            badgeClassName={'size-6 md:size-7'}
          />
        }
        marginBottom={false}
        className={Platform.OS === 'web' ? 'min-w-0 shrink' : 'leading-[1.05]'}
      />
      <H2
        title={formatCurrency(listing.price!, listing.currency!)}
        marginTop={false}
        marginBottom={false}
        className={'!font-black text-green-600 md:mt-1.5'}
      />
      <Text
        className={
          Platform.OS === 'web'
            ? 'text-brand-brown-dark/70 mt-1.5 text-sm md:text-base'
            : 'text-brand-brown-dark/70 mt-1.5 text-sm'
        }
      >
        {t('listings.show_listing.published_at', {
          date: formatDateAsTimeAgo(listing.published_at!),
        })}
      </Text>
      <InfoGrid
        className={'mt-6'}
        variant={'cream'}
        singleColumn={true}
        items={[
          {
            icon: listingFeatureIcons.breed,
            title: breedTypeTitle,
            description: breedDescription,
          },
          {
            icon: listingFeatureIcons.pedigree,
            title: t('listings.show_listing.pedigree_status'),
            description: hasPedigree
              ? t('listings.show_listing.pedigree_status_description_present')
              : t('listings.show_listing.pedigree_status_description_absent'),
          },
          {
            icon: listing.user.breeder
              ? listingFeatureIcons.breeder
              : listingFeatureIcons.privateSeller,
            title: listing.user.breeder ? t(`common:breeder`) : t(`common:private_seller`),
            description: listing.user.breeder
              ? t(`listings.show_listing.breeder_description`)
              : t(`listings.show_listing.private_seller_description`),
          },
          {
            icon: falCalendarDay,
            title: t(`listings.show_listing.birth_date`),
            description: t(`listings.show_listing.birth_date_description`, {
              birthDate: formatDateCustom(listing.birth_date!, 'd LLL yyyy'),
              age: formatDateAsAge(listing.birth_date!, false),
            }),
          },
          {
            icon: falCreditCard,
            title: t('listings.show_listing.payment'),
            description: listing.deposit
              ? t('listings.show_listing.payment_summary_with_deposit', {
                  price: formatCurrency(listing.price!, listing.currency!),
                  deposit: formatCurrency(listing.deposit, listing.currency!),
                })
              : t('listings.show_listing.payment_summary_without_deposit', {
                  price: formatCurrency(listing.price!, listing.currency!),
                }),
          },
          {
            icon: falLocationDot,
            title: t('listings.show_listing.location'),
            description: t('listings.show_listing.location_summary', {
              location: compactListingLocation,
            }),
          },
        ]}
      />

      {listing.deleted_at ? (
        <>
          <H2 title={t('listings.show_listing.deleted_listing')} />
          {Platform.OS === 'web' ? (
            <Text className={'text-brand-brown-dark md:text-lg'}>
              <Trans
                ns={'screens'}
                i18nKey={'listings.show_listing.deleted_listing_description'}
                components={{
                  listings: (
                    <Link
                      href={route(listingsRouteName)}
                      textClassName="underline font-medium"
                      skipDefaults={true}
                    />
                  ),
                }}
              />
            </Text>
          ) : (
            <View className={'gap-3'}>
              <Text className={'text-brand-brown-dark md:text-lg'}>
                {t('listings.show_listing.deleted_listing')}
              </Text>
              <Button
                text={t('listings.show_listing.view_pets_for_sale')}
                href={route(listingsRouteName)}
                color={'white'}
              />
            </View>
          )}

          {showAds && <AdBanner className={'mt-3 md:mt-6'} placement={'content'} />}
        </>
      ) : (
        <>
          <View className={Platform.OS === 'web' ? 'hidden md:block' : undefined}>
            <H2 title={t('listings.show_listing.seller')} />
            {sellerContactCard}
          </View>

          {!widgetsBelowGallery && partnerWidgets}

          <H2 title={petsTitle} />
          <View className={'-m-1.5 flex-row flex-wrap'}>
            {forSalePets.map((pet) => (
              <View key={pet.id} className={'w-1/2 p-1.5 md:w-1/3'}>
                <AnimalCard
                  image={pet.avatar}
                  imageAlt={pet.name}
                  imageTestID={`pet-image-${pet.id}`}
                  onImagePress={getPetImagePress(pet)}
                  title={t(`common:enums.gender.${pet.gender}_${listing.animal}`)}
                  subtitle={pet.name}
                  statusPet={{ ...pet, listing: listing }}
                  registrationId={normalizePedigreeRegistrationId(pet.registration_id)}
                  registryLink={getListingPetPedigreeRegistryLink(listing, pet)}
                  registryLinkTestID={`pet-registry-link-${pet.id}`}
                />
              </View>
            ))}
          </View>

          {listing.litter_insurance && <ListingLitterInsured />}

          {listing.pet_age !== PetAge.Adult && (
            <>
              <H2 title={t('listings.show_listing.parents')} />
              <View className={'-m-1.5 flex-row flex-wrap'}>
                {[PetRole.Mother, PetRole.Father].map((role) => {
                  const pet = role === PetRole.Mother ? mother : father;

                  return (
                    <View key={role} className={'w-1/2 p-1.5'}>
                      <ParentPetCard
                        role={role}
                        pet={pet}
                        registryLink={getParentRegistryLink(pet)}
                        onImagePress={getPetImagePress(pet)}
                      />
                    </View>
                  );
                })}
              </View>
            </>
          )}

          {showAds && <AdBanner className={'mt-3 md:mt-6'} placement={'content'} />}

          <H2 title={t('listings.show_listing.description')} />
          <Text className={'text-brand-brown-dark md:text-lg'}>{listing.description}</Text>

          {Platform.OS === 'web' && (
            <View className={'mt-6'}>
              <ProductFeedbackPrompt
                promptId={'listing_detail'}
                context={{
                  listing_id: listing.id,
                  animal: listing.animal,
                  breed: listing.breed_name,
                  pet_age: listing.pet_age,
                  seller_type: listing.user.breeder ? 'breeder' : 'private_seller',
                }}
              />
            </View>
          )}

          <H2
            title={t('listings.show_listing.payment')}
            linkTitle={t('common:learn_more')}
            linkHref={route('petpay.for-buyers')}
          />
          <View className={'border-brand-brown-dark/15 gap-3 rounded border bg-white p-3'}>
            <Text className={'text-brand-brown-dark font-bold'}>
              {t('listings.show_listing.payment_breakdown')}:
            </Text>

            <View className={'gap-3 rounded bg-stone-100 p-3'}>
              <View className={'flex-row'}>
                <Text className={'text-brand-brown-dark flex-1'}>
                  {t('listings.show_listing.deposit')}:
                </Text>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {formatCurrency(listing.deposit!, listing.currency!)}
                </Text>
              </View>

              <View className={'flex-row'}>
                <Text className={'text-brand-brown-dark flex-1'}>
                  {t('listings.show_listing.final_payment')}:
                </Text>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {formatCurrency(finalPayment, listing.currency!)}
                </Text>
              </View>

              <View className={'flex-row'}>
                <Text className={'text-brand-brown-dark flex-1'}>
                  {t('listings.show_listing.petpay_fee')}:
                </Text>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {formatCurrency(petPayFee, listing.currency!)}
                </Text>
              </View>

              <View className={'border-brand-brown-dark/10 flex-row border-t pt-3'}>
                <Text className={'text-brand-brown-dark flex-1 font-bold'}>
                  {t('listings.show_listing.total')}:
                </Text>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {formatCurrency(totalPayment, listing.currency!)}
                </Text>
              </View>
            </View>

            <PaymentBrands className={'justify-center pt-1'} />

            <View className={'gap-3'}>
              <View>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {t('listings.show_listing.why_petpay_fee')}
                </Text>
                <Text className={'text-brand-brown-dark/70 mt-1 text-sm md:text-base'}>
                  {t('listings.show_listing.why_petpay_fee_description')}
                </Text>
              </View>

              <InfoGrid
                items={petpayBuyerFeatures}
                descriptionClassName={'text-xs leading-4 md:text-sm md:leading-5'}
              />

              <Text
                className={'text-brand-brown-dark/70 text-xs leading-4 md:text-sm md:leading-5'}
              >
                {t('petpay.for_buyers.tractive_disclaimer')}
              </Text>

              <Button
                text={t('listings.show_listing.petpay_cta')}
                href={route('petpay.for-buyers')}
                color={'brown'}
                dense={true}
                icon={falAngleRight}
                className={'self-center'}
              />
            </View>
          </View>

          <H2
            title={t('listings.show_listing.pickup_details')}
            linkTitle={t('common:learn_more')}
            linkHref={route('buying-a-pet') + '#pickup'}
          />

          <View className={'border-brand-brown-dark/15 gap-6 rounded border bg-white p-3'}>
            <View>
              <Text className={'text-brand-brown-dark font-bold'}>
                {t(`listings.show_listing.${pickupDateLabel}`)}:
              </Text>
              <Text className={'text-brand-brown-dark'}>
                {formatDateCustom(listing.pickup_date!)}
              </Text>
            </View>

            <View>
              <Text className={'text-brand-brown-dark font-bold'}>
                {t('listings.show_listing.documents_to_receive')}:
              </Text>
              <InfoGrid
                className={'mt-3'}
                items={[
                  {
                    icon: falStamp,
                    title: t('listings.show_listing.registration_certificate'),
                  },
                  {
                    icon: falMessageMedical,
                    title: t('listings.show_listing.veterinary_certificate'),
                  },
                  {
                    icon: falSyringe,
                    title: t('listings.show_listing.vaccination_certificate'),
                  },
                  {
                    icon: falSignature,
                    title: t('listings.show_listing.purchase_agreement'),
                  },
                ]}
              />
            </View>

            <View>
              <Text className={'text-brand-brown-dark font-bold'}>
                {t('listings.show_listing.location')}:
              </Text>
              <Text className={'text-brand-brown-dark'}>{listingLocation}</Text>
              <MapPosition
                containerClassName={'aspect-4/3 mt-3 w-full'}
                imageClassName={'rounded object-cover'}
                coordinate={listing.user.approximate_location}
              />
            </View>
          </View>

          {listing.user.breeder && (
            <>
              <H2
                title={t('listings.show_listing.about_the_breeder')}
                linkTitle={t('common:learn_more')}
                linkHref={route('breeders.show', { breeder: listing.user.breeder.slug })}
              />

              <View
                className={
                  'border-brand-brown-dark/15 rounded border bg-white p-3 md:flex-row md:items-start md:gap-5'
                }
              >
                <Link
                  href={route('breeders.show', { breeder: listing.user.breeder.slug })}
                  containerClassName={'w-full md:w-1/3 md:shrink-0'}
                  asBlock={true}
                >
                  <Image
                    src={getBreederImageAsset(listing.user.breeder)}
                    alt={listing.user.breeder.name}
                    containerClassName={'aspect-4/3 w-full'}
                    imageClassName={'rounded object-cover group-hover:opacity-75 md:rounded-lg'}
                    sizeType={'large'}
                  />
                </Link>

                <View className={'mt-3 min-w-0 flex-1 md:mt-0'}>
                  <Link
                    href={route('breeders.show', { breeder: listing.user.breeder.slug })}
                    textClassName={'text-brand-brown-dark text-lg font-bold md:text-xl'}
                  >
                    {listing.user.breeder.name}
                  </Link>

                  <Text className={'text-brand-brown-dark/70 mt-1.5'}>
                    {listing.user.breeder.description}
                  </Text>
                </View>
              </View>
            </>
          )}

          {listing.breed && (
            <>
              <H2
                title={t('listings.show_listing.about_the_breed')}
                linkTitle={t('common:learn_more')}
                linkHref={route('breeds.show', { breed: listing.breed.slug })}
              />

              <View
                className={
                  'border-brand-brown-dark/15 rounded border bg-white p-3 md:flex-row md:items-start md:gap-5'
                }
              >
                <Link
                  href={route('breeds.show', { breed: listing.breed.slug })}
                  containerClassName={'w-full md:w-1/3 md:shrink-0'}
                  asBlock={true}
                >
                  <Image
                    src={listing.breed.avatar}
                    alt={listing.breed.name}
                    containerClassName={'aspect-4/3 w-full'}
                    imageClassName={'rounded object-cover group-hover:opacity-75 md:rounded-lg'}
                    sizeType={'large'}
                  />
                </Link>

                <View className={'mt-3 min-w-0 flex-1 md:mt-0'}>
                  <Link
                    href={route('breeds.show', { breed: listing.breed.slug })}
                    textClassName={'text-brand-brown-dark text-lg font-bold md:text-xl'}
                  >
                    {listing.breed.name}
                  </Link>

                  <Text className={'text-brand-brown-dark/70 mt-1.5'}>{listing.breed.summary}</Text>
                </View>
              </View>
            </>
          )}
        </>
      )}
    </View>
  );

  const similarListingsSection = (
    <>
      <H2
        title={t('listings.show_listing.similar_listings')}
        linkTitle={t('listings.show_listing.view_similar_listings')}
        linkHref={route(listingsRouteName)}
      />
      <View
        className={
          Platform.OS === 'web' ? 'flex-cols-1 md:flex-cols-4 flex-row gap-3 md:gap-6' : 'gap-3'
        }
      >
        {similarListings.map((similarListing) => (
          <View key={similarListing.id}>
            <ListingCard listing={similarListing} />
          </View>
        ))}
      </View>
    </>
  );

  if (Platform.OS === 'web') {
    return (
      <PageContent nativeSafeTop={true} className={'!py-0'}>
        {showAds && <AdBanner className={'mt-4 md:mt-6'} placement={'top'} />}

        <View className={showAds ? 'mt-6 pb-3 md:pb-12' : 'py-3 md:py-12'}>
          <View className={'gap-3 md:flex-row-reverse md:items-start md:gap-6 lg:gap-12'}>
            {mediaSection}
            {detailsSection}
          </View>
          {similarListingsSection}
          {!listing.deleted_at && (
            <View className={isLoggedIn ? 'h-56 md:hidden' : 'h-40 md:hidden'} />
          )}
        </View>
        {mobileSellerContactBar}
      </PageContent>
    );
  }

  return (
    <View>
      {mediaSection}
      <PageContent className={'px-3 pb-3 pt-1.5 md:px-6 md:pb-12 md:pt-0'}>
        <View className={'gap-6'}>
          {detailsSection}
          {similarListingsSection}
        </View>
      </PageContent>
    </View>
  );
};

function ParentPetCard({
  role,
  pet,
  registryLink,
  onImagePress,
}: {
  role: typeof PetRole.Mother | typeof PetRole.Father;
  pet: Pet | null;
  registryLink: PedigreeRegistryLink | null;
  onImagePress?: () => void;
}) {
  const { t } = useTranslation(['screens', 'components']);
  const roleLabel = t(`listings.show_listing.${role === PetRole.Mother ? 'mother' : 'father'}`);
  const parentName = pet?.name?.trim();
  const hasParentName = !!parentName && !isDefaultParentName(role, parentName, roleLabel);
  const displayName = hasParentName ? parentName : t('listings.show_listing.unknown_parent_name');

  return (
    <AnimalCard
      image={pet?.avatar}
      imageAlt={hasParentName ? `${roleLabel}: ${parentName}` : roleLabel}
      imageTestID={`parent-image-${role}`}
      onImagePress={onImagePress}
      title={roleLabel}
      subtitle={pet ? displayName : t('listings.show_listing.not_added')}
      registrationId={normalizePedigreeRegistrationId(pet?.registration_id)}
      registryLink={pet ? registryLink : null}
      registryLinkTestID={`parent-registry-link-${role}`}
    />
  );
}

function isDefaultParentName(
  role: typeof PetRole.Mother | typeof PetRole.Father,
  name: string,
  roleLabel: string,
) {
  const normalizedName = name.trim().toLowerCase();
  const defaultNames =
    role === PetRole.Mother
      ? ['mother', 'mamma', 'mor', 'tik', 'hona']
      : ['father', 'pappa', 'far', 'hane'];

  return [roleLabel.toLowerCase(), ...defaultNames].includes(normalizedName);
}

ShowListingScreen.pageBuilder = new PageBuilder<{ listing: Listing }, { listing: string }>()
  .request(({ req, params }) =>
    req<{ listing: Listing }>(apiRoute('listings.show', { listing: params.listing }), {
      include_similar_listings: 0,
    }).then((res) => ({
      listing: res.listing,
    })),
  )
  .transformProps(({ props, params, ctx }) => {
    if (!ctx.res || params.listing === props.listing.slug) {
      return props;
    }

    return {
      redirect: {
        destination: buildRoute(
          'listings.show',
          { listing: props.listing.slug },
          { lang: getSafeLanguageCode(ctx.locale) },
        ),
        statusCode: 301,
      },
    };
  })
  .deriveRouteParams((props) => ({
    listing: props.listing.slug,
  }));

export { ShowListingScreen, NativeListingContactBar };
export type { ShowListingScreenProps };
